/**
 * 睡眠レポート
 *
 * - 今日: 体調に合わせた今日の過ごし方、寝る準備、今夜の就寝・起床（寝る準備の通知から ?tab=today で開かれる）
 * - 記録: 1日分（起床時刻がその日の睡眠すべて）の解析（起床の通知から ?date=<起床日> で開かれる）
 * - 推移: 日ごとの推移と傾向
 */

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useGoBack } from '@/hooks/useGoBack'
import { useSleepStore } from '@/store/useSleepStore'

import { AiReportCard } from './components/ai-report-card'
import { BreathingChart } from './components/breathing-chart'
import { DayStrip } from './components/day-strip'
import { EpisodeList } from './components/episode-list'
import { MetricGrid } from './components/metric-grid'
import { PatternCard } from './components/pattern-card'
import { SleepBudgetCard } from './components/pre-sleep-card'
import { SleepTrends } from './components/sleep-trends'
import { TodayCard } from './components/today-card'
import { VitalsChart } from './components/vitals-chart'
import { formatDay, formatMinutes, QUALIFIER_LABEL, todayJstDate } from './format'

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

type Tab = 'today' | 'record' | 'trends'

/** 古いリンク（次の睡眠 = plan、この日 = day）も受け付ける */
const initialTab = (params: URLSearchParams): Tab => {
  const param = params.get('tab')
  if (param === 'record' || param === 'day') return 'record'
  if (param === 'trends') return 'trends'
  if (param === null && params.has('date')) return 'record'
  return 'today'
}

const DayDetail = () => {
  const { detail, isDetailLoading, isAnalyzing, analyze, isAdvisingBreathing, adviseBreathing } = useSleepStore()
  const [, setSearchParams] = useSearchParams()

  // SpO2・呼吸の対策は毎日は作らず、開いたときにまだ無ければ AI に頼む
  const needsAdvice = detail !== null && detail.breathing_actions === null && detail.breathing_insights.length > 0
  const detailDate = detail?.date
  useEffect(() => {
    if (needsAdvice && detailDate) void adviseBreathing(detailDate)
  }, [needsAdvice, detailDate, adviseBreathing])

  if (!detail) return isDetailLoading ? <Skeleton className="h-96" /> : null

  const { metrics, context } = detail
  const ranges = detail.episodes.map((e) => ({ start: e.summary.sleep_start, end: e.summary.sleep_end }))
  const goTo = (date: string | null) => {
    if (date !== null) setSearchParams({ date }, { replace: true })
  }

  return (
    <div className={isDetailLoading ? 'space-y-4 opacity-60' : 'space-y-4'}>
      <section className="rounded-xl border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <Button
            size="icon"
            variant="ghost"
            disabled={detail.prev_date === null}
            onClick={() => goTo(detail.prev_date)}
          >
            <ChevronLeft />
          </Button>
          <div className="min-w-0 flex-1 text-center">
            <p className="text-sm font-medium">{formatDay(detail.date)} に起きた睡眠</p>
            <p className="text-3xl font-bold tabular-nums">{formatMinutes(metrics.total_min)}</p>
            <p className="text-xs text-muted-foreground">
              {metrics.episode_count > 1 ? `${metrics.episode_count}回に分けて` : '1回'}
              {metrics.nap_min > 0 && `（うち昼寝 ${formatMinutes(metrics.nap_min)}）`}
            </p>
            {metrics.score !== null && (
              <p className="text-sm text-indigo-600 dark:text-indigo-300">
                スコア {metrics.score}
                {metrics.score_qualifier &&
                  `（${QUALIFIER_LABEL.get(metrics.score_qualifier) ?? metrics.score_qualifier}）`}
              </p>
            )}
          </div>
          <Button
            size="icon"
            variant="ghost"
            disabled={detail.next_date === null}
            onClick={() => goTo(detail.next_date)}
          >
            <ChevronRight />
          </Button>
        </div>
      </section>

      <AiReportCard
        ai={detail.ai}
        aiModel={detail.ai_model}
        aiStale={detail.ai_stale}
        hints={context.hints}
        isAnalyzing={isAnalyzing}
        onAnalyze={() => void analyze(detail.date)}
      />
      <MetricGrid metrics={metrics} context={context} />
      <EpisodeList episodes={detail.episodes} usualPlaceName={context.usual_place_name} />
      <VitalsChart series={detail.series} ranges={ranges} />
      <BreathingChart
        series={detail.series}
        ranges={ranges}
        insights={detail.breathing_insights}
        actions={detail.breathing_actions}
        isAdvising={isAdvisingBreathing || isAnalyzing}
      />
      <SleepBudgetCard context={context} totalMin={metrics.total_min} />
      <PatternCard patterns={context.patterns} highlightCurrent />
    </div>
  )
}

export const SleepReportPage = () => {
  const goBack = useGoBack()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>(() => initialTab(searchParams))
  const { days, fetchDays, fetchDetail, fetchPlan, fetchLastNight, error } = useSleepStore()

  const dateParam = searchParams.get('date')
  // 日付の指定が無ければ最新の日を開く（URL は書き換えない。書き換えると再読み込みで記録タブになる）
  const selectedDate = dateParam && DATE_PATTERN.test(dateParam) ? dateParam : (days.at(0)?.date ?? null)

  useEffect(() => {
    void fetchDays()
    void fetchPlan(true)
  }, [fetchDays, fetchPlan])

  useEffect(() => {
    const latest = days.at(0)
    if (latest?.date === todayJstDate()) void fetchLastNight(latest.date)
  }, [days, fetchLastNight])

  useEffect(() => {
    if (selectedDate !== null) void fetchDetail(selectedDate)
  }, [selectedDate, fetchDetail])

  const handleSelect = useCallback(
    (date: string) => {
      setSearchParams({ date }, { replace: true })
      setTab('record')
    },
    [setSearchParams],
  )

  return (
    <div className="flex h-dvh flex-col bg-background">
      <Header title="睡眠レポート" onClose={goBack} />
      <div className="flex-1 overflow-y-auto p-4 pb-20">
        <div className="mx-auto max-w-2xl space-y-4">
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList className="w-full">
              <TabsTrigger value="today">今日</TabsTrigger>
              <TabsTrigger value="record">記録</TabsTrigger>
              <TabsTrigger value="trends">推移</TabsTrigger>
            </TabsList>
            <TabsContent value="today">
              <TodayCard onOpenRecord={handleSelect} />
            </TabsContent>
            <TabsContent value="record" className="space-y-4">
              <DayStrip days={days} selectedDate={selectedDate} onSelect={handleSelect} />
              <DayDetail />
            </TabsContent>
            <TabsContent value="trends">
              <SleepTrends />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
